import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  AlertTriangle,
  ArrowLeft,
  Apple,
  CheckCircle2,
  Clock,
  CreditCard,
  ExternalLink,
  PauseCircle,
  Play,
  RefreshCw,
  ShieldCheck,
  ShieldX,
  Store,
  TestTube,
  XCircle,
} from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LoadingState } from "@/components/data/LoadingState";
import { ErrorState } from "@/components/data/ErrorState";
import { EmptyState } from "@/components/data/EmptyState";
import { JsonViewer } from "@/components/data/JsonViewer";
import { RelTime } from "@/components/data/RelTime";
import { api } from "@/lib/api";
import { qk, useSubscriptionDetail } from "@/lib/queries";
import { fmtDateTime, truncate } from "@/lib/format";
import { cn } from "@/lib/utils";
import { isEffectivePro, isStalePro } from "@/lib/subscription";
import type { SubscriptionDetailResponse, SubscriptionEventRow } from "@/types";

function storeBadge(store: string | null | undefined) {
  if (!store) return <span className="text-sm text-muted-foreground">—</span>;
  const key = store.toUpperCase();
  if (key === "APP_STORE" || key === "MAC_APP_STORE") {
    return <Badge variant="info"><Apple className="mr-1 h-3 w-3" />App Store</Badge>;
  }
  if (key === "PLAY_STORE") {
    return <Badge variant="info"><Play className="mr-1 h-3 w-3" />Play Store</Badge>;
  }
  if (key === "STRIPE" || key === "RC_BILLING") {
    return <Badge variant="info"><CreditCard className="mr-1 h-3 w-3" />{store}</Badge>;
  }
  if (key === "PROMOTIONAL") return <Badge variant="default">Promo</Badge>;
  return <Badge variant="default"><Store className="mr-1 h-3 w-3" />{store}</Badge>;
}

function fmtMs(ms: number | null | undefined): string {
  if (ms == null) return "—";
  return fmtDateTime(new Date(ms).toISOString());
}

function parseAliases(value: string | null | undefined): string[] {
  if (!value) return [];
  try {
    const arr = JSON.parse(value);
    return Array.isArray(arr) ? arr.filter((v): v is string => typeof v === "string") : [];
  } catch {
    return [];
  }
}

function parsePayload(payload: string): unknown {
  try {
    return JSON.parse(payload);
  } catch {
    return payload;
  }
}

export function SubscriptionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const userId = id ?? null;
  const q = useSubscriptionDetail(userId);
  const qc = useQueryClient();

  const resync = useMutation({
    mutationFn: () => api.post<{ ok: boolean }>(`/api/admin/subscriptions/${userId}/sync`),
    onSuccess: () => {
      toast.success("Pulled fresh state from RevenueCat.");
      qc.invalidateQueries({ queryKey: qk.subscriptionDetail(userId ?? "") });
      qc.invalidateQueries({ queryKey: ["subscriptions"] });
      qc.invalidateQueries({ queryKey: ["subscription-events"] });
      qc.invalidateQueries({ queryKey: qk.subscriptionStats });
      qc.invalidateQueries({ queryKey: qk.userFull(userId ?? "") });
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Resync failed");
    },
  });

  if (!userId) return <ErrorState error={new Error("Missing user id")} />;

  if (q.isLoading) {
    return (
      <>
        <PageHeader title="Subscription" />
        <LoadingState />
      </>
    );
  }

  if (q.isError || !q.data) {
    return (
      <>
        <PageHeader title="Subscription" />
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      </>
    );
  }

  const data: SubscriptionDetailResponse = q.data;
  const sub = data.subscription;
  const user = data.user;
  const displayName = user?.name ?? sub?.name ?? "User";
  const email = user?.email ?? sub?.email ?? null;

  return (
    <>
      <PageHeader
        title={displayName}
        description={
          <span className="flex items-center gap-2 text-sm text-muted-foreground">
            <Link to="/subscriptions" className="inline-flex items-center gap-1 hover:underline">
              <ArrowLeft className="h-3.5 w-3.5" /> Subscriptions
            </Link>
            <span>·</span>
            <Link to={`/users/${userId}`} className="hover:underline">User detail</Link>
            {email ? <><span>·</span><span className="font-mono text-xs">{email}</span></> : null}
          </span>
        }
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => resync.mutate()}
              disabled={resync.isPending}
            >
              <RefreshCw className={cn("mr-2 h-3.5 w-3.5", resync.isPending && "animate-spin")} />
              Resync from RevenueCat
            </Button>
            {sub?.rc_app_user_id ? (
              <a
                href={`https://app.revenuecat.com/customers/${encodeURIComponent(sub.rc_app_user_id)}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border/60 bg-secondary px-3 text-xs font-medium hover:bg-secondary/80"
              >
                Open in RevenueCat <ExternalLink className="h-3 w-3" />
              </a>
            ) : null}
          </div>
        }
      />

      {!sub ? (
        <EmptyState
          title="No subscription data yet"
          description="This user has never been seen by RevenueCat. The row will appear automatically after they purchase or after iOS calls Purchases.logIn(user.id)."
        />
      ) : (
        <div className="space-y-4">
          <EntitlementCard sub={sub} />

          <Tabs defaultValue="details">
            <TabsList>
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="events">Events ({data.events.length})</TabsTrigger>
              <TabsTrigger value="raw">Raw payload</TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="mt-4">
              <DetailsGrid sub={sub} userId={userId} />
            </TabsContent>

            <TabsContent value="events" className="mt-4">
              <EventsTimeline events={data.events} />
            </TabsContent>

            <TabsContent value="raw" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium">
                    Latest cached RevenueCat payload
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {sub.rc_raw ? (
                    <JsonViewer value={parsePayload(sub.rc_raw)} />
                  ) : (
                    <span className="text-sm text-muted-foreground">No payload cached yet.</span>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      )}
    </>
  );
}

function EntitlementCard({ sub }: { sub: SubscriptionDetailResponse["subscription"] }) {
  if (!sub) return null;
  const pro = isEffectivePro(sub);
  const status = isStalePro(sub)
    ? { label: "Expired (stale is_pro)", tone: "error" as const, icon: <ShieldX className="h-4 w-4" /> }
    : pro && sub.is_in_grace_period
      ? { label: "Active (grace period)", tone: "warn" as const, icon: <PauseCircle className="h-4 w-4" /> }
      : pro && sub.billing_issues_detected
        ? { label: "Active with billing issue", tone: "warn" as const, icon: <AlertTriangle className="h-4 w-4" /> }
        : pro && sub.unsubscribe_detected
          ? { label: "Active (will not renew)", tone: "warn" as const, icon: <Clock className="h-4 w-4" /> }
          : pro
            ? { label: "Active", tone: "success" as const, icon: <ShieldCheck className="h-4 w-4" /> }
            : { label: "Inactive", tone: "error" as const, icon: <ShieldX className="h-4 w-4" /> };

  return (
    <Card
      className={cn(
        "border-l-4",
        status.tone === "success" ? "border-l-emerald-500/60" :
        status.tone === "warn" ? "border-l-amber-500/60" :
        "border-l-rose-500/60"
      )}
    >
      <CardContent className="flex flex-wrap items-center gap-x-6 gap-y-3 p-5">
        <div className="flex items-center gap-2">
          {status.icon}
          <span className="text-lg font-semibold">{status.label}</span>
        </div>
        <Separator orientation="vertical" className="h-6" />
        <div className="flex flex-col">
          <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Entitlement</span>
          <span className="font-mono text-sm">{sub.entitlement_id ?? "—"}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Product</span>
          <span className="font-mono text-sm">{sub.product_id ? truncate(sub.product_id, 40) : "—"}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Store</span>
          <span>{storeBadge(sub.store)}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Environment</span>
          <span>
            {sub.is_sandbox ? (
              <Badge variant="warn" className="font-mono text-[10px]"><TestTube className="mr-1 h-3 w-3" />SANDBOX</Badge>
            ) : (
              <Badge variant="default" className="font-mono text-[10px]">{sub.environment ?? "—"}</Badge>
            )}
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Expires</span>
          <span className="text-sm">{fmtMs(sub.expires_at_ms)}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Will renew</span>
          <span>
            {sub.will_renew === true ? (
              <Badge variant="success" className="text-[10px]"><CheckCircle2 className="mr-1 h-3 w-3" />Yes</Badge>
            ) : sub.will_renew === false ? (
              <Badge variant="warn" className="text-[10px]"><XCircle className="mr-1 h-3 w-3" />No</Badge>
            ) : (
              <span className="text-sm text-muted-foreground">—</span>
            )}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

function DetailsGrid({ sub, userId }: { sub: NonNullable<SubscriptionDetailResponse["subscription"]>; userId: string }) {
  const aliases = parseAliases(sub.aliases ?? null);
  const rows: Array<{ label: string; value: React.ReactNode }> = [
    { label: "BillyPocket user id", value: <Link to={`/users/${userId}`} className="font-mono text-xs text-sky-300 hover:underline">{userId}</Link> },
    { label: "RC app_user_id", value: <span className="font-mono text-xs">{sub.rc_app_user_id}</span> },
    { label: "RC original_app_user_id", value: <span className="font-mono text-xs">{sub.rc_original_app_user_id ?? "—"}</span> },
    { label: "Aliases", value: aliases.length > 0 ? (
      <div className="flex flex-wrap gap-1">
        {aliases.map((a) => (
          <span key={a} className="rounded-md bg-secondary px-1.5 py-0.5 font-mono text-[11px]">
            {a.startsWith("$RCAnonymousID:") ? <><span className="text-muted-foreground">anon:</span>{a.slice(15, 22)}…</> : truncate(a, 36)}
          </span>
        ))}
      </div>
    ) : <span className="text-xs text-muted-foreground">—</span> },
    { label: "Period type", value: <span className="font-mono text-xs">{sub.period_type ?? "—"}</span> },
    { label: "Purchased at", value: <span className="text-xs">{fmtMs(sub.purchased_at_ms ?? null)}</span> },
    { label: "Original purchase", value: <span className="text-xs">{fmtMs(sub.original_purchase_at_ms ?? null)}</span> },
    { label: "Grace period ends", value: <span className="text-xs">{fmtMs(sub.grace_period_expires_ms ?? null)}</span> },
    { label: "Unsubscribe detected", value: <BoolLabel value={sub.unsubscribe_detected} /> },
    { label: "Billing issues detected", value: <BoolLabel value={sub.billing_issues_detected} /> },
    { label: "Latest event", value: (
      <div className="flex flex-col gap-0.5">
        <span className="font-mono text-xs">{sub.latest_event_type ?? "—"}</span>
        {sub.latest_event_at_ms ? (
          <span className="text-[10px] text-muted-foreground">
            <RelTime value={sub.latest_event_at_ms} /> · {fmtMs(sub.latest_event_at_ms)}
          </span>
        ) : null}
        {sub.latest_event_id ? (
          <span className="text-[10px] font-mono text-muted-foreground">id: {sub.latest_event_id}</span>
        ) : null}
      </div>
    ) },
    { label: "Row updated", value: <span className="text-xs"><RelTime value={sub.updated_at} /></span> },
  ];

  return (
    <Card>
      <CardContent className="p-0">
        <dl className="divide-y divide-border/60">
          {rows.map((row) => (
            <div key={row.label} className="grid grid-cols-1 gap-1 px-5 py-3 sm:grid-cols-[200px_1fr] sm:gap-3">
              <dt className="text-[11px] uppercase tracking-wider text-muted-foreground">{row.label}</dt>
              <dd className="min-w-0">{row.value}</dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  );
}

function BoolLabel({ value }: { value: boolean | null | undefined }) {
  if (value === true) return <Badge variant="warn" className="text-[10px]">Yes</Badge>;
  if (value === false) return <Badge variant="success" className="text-[10px]">No</Badge>;
  return <span className="text-xs text-muted-foreground">—</span>;
}

function EventsTimeline({ events }: { events: SubscriptionEventRow[] }) {
  const [expandedId, setExpandedId] = useState<number | null>(null);

  if (events.length === 0) {
    return (
      <EmptyState
        title="No events"
        description="This user has no RevenueCat events on file yet."
      />
    );
  }

  return (
    <Card>
      <CardContent className="p-0">
        <ol className="divide-y divide-border/60">
          {events.map((event) => {
            const expanded = expandedId === event.id;
            const tone =
              event.event_type === "TEST"
                ? "default"
                : event.error
                  ? "error"
                  : event.event_type.includes("ISSUE") || event.event_type === "EXPIRATION" || event.event_type === "CANCELLATION"
                    ? "warn"
                    : event.event_type === "INITIAL_PURCHASE" || event.event_type === "RENEWAL" || event.event_type === "UNCANCELLATION"
                      ? "success"
                      : "info";

            return (
              <li key={event.id} className="px-5 py-3">
                <button
                  type="button"
                  className="flex w-full items-center gap-3 text-left"
                  onClick={() => setExpandedId(expanded ? null : event.id)}
                >
                  <Badge variant={tone} className="font-mono text-[10px]">{event.event_type}</Badge>
                  <span className="font-mono text-[10px] text-muted-foreground">{event.trigger}</span>
                  <span className="text-xs text-muted-foreground">
                    <RelTime value={event.event_at_ms ?? event.created_at} />
                  </span>
                  <span className="text-[10px] text-muted-foreground">
                    {fmtMs(event.event_at_ms ?? event.created_at)}
                  </span>
                  {event.environment ? (
                    <Badge variant={event.environment === "SANDBOX" ? "warn" : "default"} className="ml-auto font-mono text-[10px]">
                      {event.environment}
                    </Badge>
                  ) : null}
                  {event.processed ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                  ) : event.error ? (
                    <XCircle className="h-4 w-4 text-rose-400" />
                  ) : null}
                </button>

                {event.error ? (
                  <div className="mt-2 rounded-md border border-rose-500/30 bg-rose-500/5 p-2 text-xs text-rose-200">
                    <span className="font-medium">Error: </span>
                    <span className="font-mono">{event.error}</span>
                  </div>
                ) : null}

                {expanded ? (
                  <div className="mt-3 space-y-3">
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-4">
                      {event.event_id ? (
                        <Field label="Event id">
                          <span className="font-mono text-[10px]">{event.event_id}</span>
                        </Field>
                      ) : null}
                      <Field label="RC app_user_id">
                        <span className="font-mono text-[10px]">{truncate(event.rc_app_user_id, 22)}</span>
                      </Field>
                      {event.product_id ? (
                        <Field label="Product">
                          <span className="font-mono text-[10px]">{event.product_id}</span>
                        </Field>
                      ) : null}
                      {event.entitlement_id ? (
                        <Field label="Entitlement">
                          <span className="font-mono text-[10px]">{event.entitlement_id}</span>
                        </Field>
                      ) : null}
                      {event.store ? (
                        <Field label="Store">
                          {storeBadge(event.store)}
                        </Field>
                      ) : null}
                    </div>
                    <div>
                      <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                        Payload
                      </div>
                      <JsonViewer value={parsePayload(event.payload)} />
                    </div>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ol>
      </CardContent>
    </Card>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
      <span>{children}</span>
    </div>
  );
}
